import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import config from "../../config";
import DeleteModal from "../common/DeleteModal";
import { getPlaceConfig } from "../dashboard/functions.js";
import useConfiguredGoogleMap from "../../hooks/useConfiguredGoogleMap.js";
import { useTranslation } from "react-i18next";

const libraries = ["places"];

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#E8EAEE",
  borderRadius: "10px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  position: "relative",
}));

const MapContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#f0f0f0",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #183059",
  overflow: "hidden",
  position: "relative",
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 10,
  width: "90%",
  maxWidth: "400px",
}));

const SearchTextField = styled("input")(({ theme }) => ({
  width: "100%",
  padding: "12px 16px 12px 44px",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  border: "1px solid #C8CBD0",
  fontSize: "14px",
  outline: "none",
  "&:hover": {
    borderColor: "#183059",
  },
  "&:focus": {
    borderColor: "#183059",
    boxShadow: "0 0 0 2px rgba(24, 48, 89, 0.1)",
  },
}));

const SearchIconWrapper = styled("div")({
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#414958",
  pointerEvents: "none",
});

const StyledTextField = styled("input")(({ error }) => ({
  width: "100%",
  padding: "16px 24px",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  border: `2px solid ${error ? "#d32f2f" : "#183059"}`,
  fontSize: "16px",
  outline: "none",
  fontFamily: "Roboto",
  "&:hover": {
    borderColor: error ? "#d32f2f" : "#183059",
  },
  "&:focus": {
    borderColor: error ? "#d32f2f" : "#183059",
    boxShadow: `0 0 0 2px ${error ? "rgba(211, 47, 47, 0.1)" : "rgba(24, 48, 89, 0.1)"}`,
  },
  "&::placeholder": {
    color: "#95A0B3",
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  marginLeft: "14px",
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "55px",
  border: "1px solid #414958",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "20px",
  "&:hover": {
    backgroundColor: "#FBC02D",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F2F2F3",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  border: "1px solid #414958",
  color: "#1F242E",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#E0E0E1",
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#cc0000",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(204, 0, 0, 0.05)",
  },
}));

const RequiredAsterisk = () => (
  <Box component="span" sx={{ color: "#d32f2f" }}>
    *
  </Box>
);

const sanitizeAlphaNumericId = (value) =>
  value.replace(/[^a-zA-Z0-9-_]/g, "");
const sanitizeStopName = (value) => value.replace(/[^a-zA-Z0-9\s]/g, "");

export default function CreateStopForm({
  onClose,
  onSubmit,
  onDelete,
  initialData = null,
  isEdit = false,
}) {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const [formData, setFormData] = useState(
    initialData || {
      stopId: "",
      name: "",
      longitude: "",
      latitude: "",
      landmark: "",
      description: "",
    }
  );

  const [errors, setErrors] = useState({
    stopId: "",
    name: "",
    longitude: "",
    latitude: "",
  });

  const [touched, setTouched] = useState({
    stopId: false,
    name: false,
    longitude: false,
    latitude: false,
  });

  const [map, setMap] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(
    initialData?.latitude && initialData?.longitude
      ? {
          lat: parseFloat(initialData.latitude),
          lng: parseFloat(initialData.longitude),
        }
      : null
  );
  const [autocomplete, setAutocomplete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { placeMarkers, getMapOptions } = useConfiguredGoogleMap({
    map,
    isLoaded,
  });

  // Refs for tracking editing state
  const isUserEditing = useRef(false);
  const editingField = useRef(null);
  const initialLoadComplete = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      initialLoadComplete.current = true;
    }, 500);
  }, []);

  const validateField = (field, value) => {
    switch (field) {
      case "stopId":
        if (!value || value.trim() === "") {
          return t("Stop ID is required");
        }
        if (!/^[a-zA-Z0-9-_]+$/.test(value.trim())) {
          return t(
            "Stop ID can contain only letters, numbers, hyphen, and underscore",
          );
        }
        return "";
      
      case "name":
        if (!value || value.trim() === "") {
          return t("Stop Name is required");
        }
        if (!/^[a-zA-Z0-9\s]+$/.test(value.trim())) {
          return t("Stop Name can contain only letters, numbers, and spaces");
        }
        return "";
      
      case "latitude":
        if (!value || value.trim() === "") {
          return t("Latitude is required");
        }
        const lat = parseFloat(value);
        if (isNaN(lat)) {
          return t("Latitude must be a valid number");
        }
        if (lat < -90 || lat > 90) {
          return t("Latitude must be between -90 and 90");
        }
        return "";
      
      case "longitude":
        if (!value || value.trim() === "") {
          return t("Longitude is required");
        }
        const lng = parseFloat(value);
        if (isNaN(lng)) {
          return t("Longitude must be a valid number");
        }
        if (lng < -180 || lng > 180) {
          return t("Longitude must be between -180 and 180");
        }
        return "";
      
      default:
        return "";
    }
  };

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);

      if (formData.latitude && formData.longitude) {
        const position = {
          lat: parseFloat(formData.latitude),
          lng: parseFloat(formData.longitude),
        };
        mapInstance.setCenter(position);
        mapInstance.setZoom(15);
      }
    },
    [formData.latitude, formData.longitude]
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onLoadAutocomplete = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const position = { lat, lng };

        setMarkerPosition(position);
        setFormData((prev) => ({
          ...prev,
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6),
        }));

        setErrors((prev) => ({
          ...prev,
          latitude: "",
          longitude: "",
        }));

        if (map) {
          map.panTo(position);
          map.setZoom(15);
        }
      }
    }
  };

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));

    setErrors((prev) => ({
      ...prev,
      latitude: "",
      longitude: "",
    }));
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });

    // Real-time validation
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    // Update marker when coordinates change
    if ((field === "latitude" || field === "longitude") && !isUserEditing.current) {
      const lat = field === "latitude" ? parseFloat(value) : parseFloat(formData.latitude);
      const lng = field === "longitude" ? parseFloat(value) : parseFloat(formData.longitude);

      const latStr = String(lat);
      const lngStr = String(lng);
      const isIncompleteDecimal = latStr.endsWith(".") || lngStr.endsWith(".");

      if (lat && lng && !isIncompleteDecimal && !isNaN(lat) && !isNaN(lng)) {
        const position = { lat, lng };
        setMarkerPosition(position);
        if (map) {
          map.panTo(position);
          map.setZoom(15);
        }
      }
    }
  };

  const handleFocus = (field) => () => {
    editingField.current = field;
    isUserEditing.current = true;
  };

  const handleBlur = (field) => () => {
    setTimeout(() => {
      editingField.current = null;
      isUserEditing.current = false;
    }, 200);

    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleSubmit = async () => {
    const requiredFields = ["stopId", "name", "latitude", "longitude"];
    const newTouched = { ...touched };
    const newErrors = { ...errors };

    requiredFields.forEach((field) => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });

    setTouched(newTouched);
    setErrors(newErrors);

    const hasErrors = requiredFields.some((field) => newErrors[field]);

    if (hasErrors) {
      return;
    }

    setSubmitError("");

    if (onSubmit) {
      try {
        await onSubmit(formData);
        if (!isEdit) {
          setFormData({
            stopId: "",
            name: "",
            longitude: "",
            latitude: "",
            landmark: "",
            description: "",
          });
          setMarkerPosition(null);
          setErrors({ stopId: "", name: "", longitude: "", latitude: "" });
          setTouched({ stopId: false, name: false, longitude: false, latitude: false });
        }
      } catch (error) {
        const rawMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "";
        // Backend sends a generic "fill all required fields" message for
        // duplicate / constraint violations — map it to something meaningful.
        const isDuplicate =
          error.response?.status === 409 ||
          rawMessage.toLowerCase().includes("duplicate") ||
          rawMessage.toLowerCase().includes("already exist") ||
          rawMessage.toLowerCase().includes("fill all the required") ||
          rawMessage.toLowerCase().includes("unique");
        const message = isDuplicate
          ? t(
              "A stop with the same location ({{latitude}}, {{longitude}}) is already present. Please choose a different location.",
              {
                latitude: formData.latitude,
                longitude: formData.longitude,
              },
            )
          : rawMessage || t("Failed to save stop. Please try again.");
        setSubmitError(message);
      }
    }
  };

  if (loadError) {
    return (
      <FormContainer>
        <Typography color="error" align="center">
          {t("Error loading Google Maps. Please check your API key.")}
        </Typography>
      </FormContainer>
    );
  }

  if (!isLoaded) {
    return (
      <FormContainer>
        <Typography align="center">{t("Loading map...")}</Typography>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Grid container spacing={3}>
        {/* Column 1: Stop ID, Longitude, Landmark */}
        <Grid item xs={12} md={3.5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("ID")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                value={formData.stopId}
                onChange={handleChange("stopId")}
                onFocus={handleFocus("stopId")}
                onBlur={handleBlur("stopId")}
                placeholder={t("Stop ID")}
                disabled={isEdit}
                error={touched.stopId && !!errors.stopId}
              />
              {touched.stopId && errors.stopId && (
                <ErrorText>{errors.stopId}</ErrorText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("Longitude")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                value={formData.longitude}
                onChange={handleChange("longitude")}
                onFocus={handleFocus("longitude")}
                onBlur={handleBlur("longitude")}
                placeholder={t("Longitude")}
                type="number"
                step="0.000001"
                error={touched.longitude && !!errors.longitude}
              />
              {touched.longitude && errors.longitude && (
                <ErrorText>{errors.longitude}</ErrorText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Landmark")}
              </Typography>
              <StyledTextField
                value={formData.landmark}
                onChange={handleChange("landmark")}
                onFocus={handleFocus("landmark")}
                onBlur={handleBlur("landmark")}
                placeholder={t("Landmark")}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Column 2: Stop Name, Latitude, Description */}
        <Grid item xs={12} md={3.5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("Name")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                value={formData.name}
                onChange={handleChange("name")}
                onFocus={handleFocus("name")}
                onBlur={handleBlur("name")}
                placeholder={t("Stop Name")}
                error={touched.name && !!errors.name}
              />
              {touched.name && errors.name && (
                <ErrorText>{errors.name}</ErrorText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("Latitude")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                value={formData.latitude}
                onChange={handleChange("latitude")}
                onFocus={handleFocus("latitude")}
                onBlur={handleBlur("latitude")}
                placeholder={t("Latitude")}
                type="number"
                step="0.000001"
                error={touched.latitude && !!errors.latitude}
              />
              {touched.latitude && errors.latitude && (
                <ErrorText>{errors.latitude}</ErrorText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Description")}
              </Typography>
              <StyledTextField
                value={formData.description}
                onChange={handleChange("description")}
                onFocus={handleFocus("description")}
                onBlur={handleBlur("description")}
                placeholder={t("Description")}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Column 3: Map */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "10px",
              mb: 1,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 7.72367V3.99967H11.3333V2.66634C11.3333 2.31272 11.1929 1.97358 10.9428 1.72353C10.6928 1.47348 10.3536 1.33301 10 1.33301H6C5.64638 1.33301 5.30724 1.47348 5.05719 1.72353C4.80714 1.97358 4.66667 2.31272 4.66667 2.66634V3.99967H6V7.72367L4.19533 8.86167C4.13331 8.92348 4.08411 8.99694 4.05059 9.07783C4.01707 9.15872 3.99987 9.24544 4 9.33301V10.6663C4 10.8432 4.07024 11.0127 4.19526 11.1377C4.32029 11.2628 4.48986 11.333 4.66667 11.333H7.33333V13.333L8 14.6663L8.66667 13.333V11.333H11.3333C11.5101 11.333 11.6797 11.2628 11.8047 11.1377C11.9298 11.0127 12 10.8432 12 10.6663V9.33301C12.0001 9.24544 11.9829 9.15872 11.9494 9.07783C11.9159 8.99694 11.8667 8.92348 11.8047 8.86167L10 7.72367Z"
                fill="#EE3B2B"
              />
            </svg>
            <Typography
              sx={{
                color: "#647490",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
              }}
            >
              {t("Click to select stop on the map.")}
            </Typography>
          </Box>
          <MapContainer>
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "298px",
                borderRadius: "16px",
              }}
              center={
                markerPosition || {
                  lat: 12.9716,
                  lng: 77.5946,
                }
              }
              zoom={markerPosition ? 15 : 12}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={handleMapClick}
              options={getMapOptions({
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: true,
                scaleControl: true,
                streetViewControl: true,
                rotateControl: true,
                fullscreenControl: true,
              })}
            >
              {placeMarkers.map((place) => {
                const config = getPlaceConfig(place.type);
                if (!config?.icon) return null;

                return (
                  <Marker
                    key={place.id}
                    position={place.position}
                    title={place.name}
                    icon={{
                      url: config.icon,
                      scaledSize: new window.google.maps.Size(32, 32),
                    }}
                  />
                );
              })}

              <Autocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={onPlaceChanged}
              >
                <SearchBox>
                  <div style={{ position: "relative" }}>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <SearchTextField
                      placeholder={t("Search location")}
                      type="text"
                    />
                  </div>
                </SearchBox>
              </Autocomplete>

              {markerPosition && (
                <Marker
                  icon={{
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(`
                      <svg width="67" height="88" viewBox="0 0 67 88" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <ellipse cx="6.52714" cy="6.52913" rx="6.52714" ry="6.52913" transform="matrix(1 0 -0.000176161 1 27.002 73.3271)" fill="white"/>
                        <path d="M33.5289 75.3271C36.0285 75.3273 38.0559 77.3546 38.0555 79.8564C38.055 82.3582 36.0269 84.3856 33.5273 84.3857C31.0277 84.3857 29.0005 82.3583 29.0008 79.8564C29.0012 77.3545 31.0293 75.3271 33.5289 75.3271Z" stroke="#20A8E0" stroke-width="4"/>
                        <path d="M34.7744 0C49.8593 0 57.4016 0.000262456 62.0879 4.68652C66.7742 9.37282 66.7744 16.9151 66.7744 32V34.7979C66.7744 49.8828 66.7741 57.425 62.0879 62.1113C58.3798 65.8194 52.8836 66.5904 43.2773 66.752C43.0885 66.7805 42.8631 66.7978 42.5977 66.7979C36.4493 66.7979 33.9789 74.8073 33.9629 74.8594C33.9594 74.8047 33.4412 66.7982 25.3281 66.7979C25.0518 66.7978 24.8037 66.7875 24.582 66.7695C14.3006 66.6458 8.53203 65.9568 4.68652 62.1113C0.00031205 57.425 0 49.8827 0 34.7979V32C0 16.9151 0.000233207 9.37281 4.68652 4.68652C9.37282 0.000231985 16.9151 0 32 0H34.7744Z" fill="#183059"/>
                        <g clip-path="url(#clip0_11783_45943)">
                          <path d="M48.3542 34.5507C48.3542 46.6435 32.8119 57.0087 32.8119 57.0087C32.8119 57.0087 17.2695 46.6435 17.2695 34.5507C17.2695 30.4272 18.907 26.4726 21.8218 23.5568C24.7365 20.641 28.6898 19.0029 32.8119 19.0029C36.934 19.0029 40.8872 20.641 43.802 23.5568C46.7167 26.4726 48.3542 30.4272 48.3542 34.5507Z" stroke="#E9E9E9" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M32.8116 39.7334C35.6729 39.7334 37.9924 37.413 37.9924 34.5508C37.9924 31.6885 35.6729 29.3682 32.8116 29.3682C29.9504 29.3682 27.6309 31.6885 27.6309 34.5508C27.6309 37.413 29.9504 39.7334 32.8116 39.7334Z" stroke="#E9E9E9" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_11783_45943">
                            <rect width="41.4462" height="41.4608" fill="white" transform="translate(12.6641 17.2754)"/>
                          </clipPath>
                        </defs>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(67, 88),
                    anchor: new window.google.maps.Point(33.5, 88),
                  }}
                  position={markerPosition}
                  draggable={true}
                  onDragEnd={(event) => {
                    const lat = event.latLng.lat();
                    const lng = event.latLng.lng();
                    setMarkerPosition({ lat, lng });
                    setFormData((prev) => ({
                      ...prev,
                      latitude: lat.toFixed(6),
                      longitude: lng.toFixed(6),
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      latitude: "",
                      longitude: "",
                    }));
                  }}
                />
              )}
            </GoogleMap>
          </MapContainer>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={6}
      >
        <Box>
          {isEdit && onDelete && (
            <DeleteButton
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
            >
              {t("Delete")}
            </DeleteButton>
          )}
        </Box>
        <Box display="flex" gap={2}>
          {!isEdit && <CancelButton onClick={onClose}>{t("Cancel")}</CancelButton>}
          <ConfirmButton onClick={handleSubmit}>
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box></Box>
      </Box>

      {submitError && (
        <ErrorText sx={{ mt: 2, textAlign: "center", fontSize: "14px" }}>
          {submitError}
        </ErrorText>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Stop!")}
        message={t(
          "Are you sure you want to delete this stop? This action cannot be undone.",
        )}
      />
    </FormContainer>
  );
}
